import { useSQLite } from 'vue-sqlite-hook'
import { capSQLiteValues, SQLiteDBConnection } from '@capacitor-community/sqlite'
import { defineCustomElements as jeepSqlite, applyPolyfills } from 'jeep-sqlite/loader'
import { Platform } from 'quasar'
import { ref } from 'vue'
import { useQueue } from '../composables/useQueue'
import { addBreadcrumb, captureException } from '@sentry/vue'
import { Capacitor } from '@capacitor/core'

const USE_QUEUE = true

void applyPolyfills().then(() => {
  void jeepSqlite(window)
})

const DB_NAME = 'db_ME_FHSNG_'

const db = useSQLite()
const connection = ref<SQLiteDBConnection | null>()

// Queue logic
const { queueError, queueSuccess, addToQueue } = useQueue({ networkRequired: false })

export function useDatabase() {
  async function openDb() {
    const isConn = await db.isConnection(DB_NAME).catch((error) => console.error(error))
    if (isConn && isConn.result) {
      await db
        .retrieveConnection(DB_NAME)
        .then((res) => {
          connection.value = res
        })
        .catch((error) => {
          console.error(`Error retrieving connection to DB: ${error}`)
          addBreadcrumb({ category: 'sqlite', message: "Can't connect to DB", level: 'debug' })
          captureException(error)
        })
    } else {
      await db
        .createConnection(DB_NAME, false)
        .then((res) => {
          connection.value = res
        })
        .catch((error) => {
          console.error(`Error loading DB: ${error}`)
          captureException(error)
        })
    }
  }

  async function loadDb() {
    // Handle initializing the webstore if we're not on mobile
    // This code is modified from examples as seen in
    // https://github.com/jepiqueau/vuevite-app/blob/master/src/main.js
    // const platform = await getPlatform()

    if (!Capacitor.isNativePlatform()) {
      const jeepEl = document.querySelector('jeep-sqlite')
      if (jeepEl != null) {
        document.body.removeChild(jeepEl)
      }
      // Create the 'jeep-sqlite' Stencil component
      const jeepSqlite = document.createElement('jeep-sqlite')

      //need to set to where we build relative to app
      //todo: build the app dir (/web/spa) into app
      if (process.env.NODE_ENV !== 'development') {
        jeepSqlite.setAttribute('wasmPath', '/web/spa/assets')
      }
      document.body.appendChild(jeepSqlite)
      await customElements.whenDefined('jeep-sqlite')
      await db.initWebStore().catch((error) => {
        console.error(`Error init web store: ${error}`)
        captureException(error)
      })
    }

    await openDb().catch((error) => console.error(error))
  }

  async function query(query: string) {
    if (!connection.value) {
      console.log('No connection found, opening db')
      await openDb().catch((error) => console.error(error))
    }
    let queryResults = null
    await connection.value?.open().catch((error) => console.error(error))
    if (connection.value) {
      queryResults = await connection.value!.query(query).catch((error) => {
        console.error(`Error with query statement: ${error}`, query)
        addBreadcrumb({ category: 'sqlite', message: `Query: ${query}`, level: 'debug' })
        captureException(error)
      })
      await closeConnection()
    }
    if (queryResults) {
      return getResultsPath(queryResults)
    }
    return queryResults
  }

  const getResultsPath = (res: capSQLiteValues) => {
    if (Platform.is.platform === 'iphone') {
      // The first item is meta on iphone, so remove it
      res.values!.shift()
      return res.values!
    }
    return res.values!
  }
  const execute = async (query: string) => {
    const fn = async () => {
      if (!connection.value) {
        console.log('No connection found, opening db')
        await openDb().catch((error) => console.error(error))
      }
      await connection.value?.open().catch((error) => console.error(error))
      await connection.value!.execute(query).catch((error) => {
        console.error(`Error with execute statement: ${error}`, query)
        addBreadcrumb({
          category: 'sqlite',
          message: `Execute statement: ${query}`,
          level: 'debug',
        })
        captureException(error)
      })
      await closeConnection()
    }
    if (USE_QUEUE) {
      await addToQueue([], fn)
        .then(() => {
          queueSuccess()
        })
        .catch(() => {
          queueError()
        })
    } else {
      await fn()
    }
  }

  const run = async (statement: string, values: any) => {
    const fn = async () => {
      if (!connection.value) {
        console.log('No connection found, opening db')
        await openDb().catch((error) => console.error(error))
      }
      await connection.value?.open().catch((error) => console.error(error))
      await connection
        .value!.run(statement, values)
        .then((res) => {
          if (res && res.changes && res.changes.changes! < 0) {
            console.trace()
            console.error({ statement, values, res })
            addBreadcrumb({ category: 'sqlite', message: 'Changes is less than 1', level: 'error' })
            captureException('Changes is less than 1')
          }
          if (res && res.changes && res.changes.lastId! < 0) {
            console.trace()
            console.error({ statement, values, res })
            addBreadcrumb({ category: 'sqlite', message: 'lastId is less than 1', level: 'error' })
            captureException('Changes is less than 1')
          }
        })
        .catch((error) => {
          console.error(`Error with run statement: ${error}`, statement, values)
          addBreadcrumb({
            category: 'sqlite',
            message: `Run statement: ${statement}, \n values: ${values}`,
            level: 'debug',
          })
          captureException(error)
        })
      await closeConnection()
    }
    if (USE_QUEUE) {
      await addToQueue([], fn)
        .then(() => {
          queueSuccess()
        })
        .catch(() => {
          queueError()
        })
    } else {
      await fn()
    }
  }

  const closeConnection = async () => {
    if (await connection.value?.isDBOpen()) {
      if (!Capacitor.isNativePlatform()) {
        await connection.value?.close().catch((error) => console.error(error))
      }
    }
  }

  return { loadDb, query, execute, run }
}
